import { AlertCircle, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";

import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import useTranslations from "@/shared/lang/useTranslations";
import { minorUnitsFor } from "@/shared/utils/currencyMinorUnits";

import type { PayoutBatchDetail, PayoutBatchItem } from "../../types/payoutBatches.types";
import {
  batchDestinationBeneficiary,
  batchDestinationLabel,
  batchDestinationValue,
} from "../../utils/batchDestination";
import { batchPath, payoutActivityPath } from "../../utils/batchLinks";
import { ItemStatusPill } from "../detail/ItemStatusPill";

export interface BatchItemDrawerProps {
  item: PayoutBatchItem | null;
  batch: PayoutBatchDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h3 className="font-mono text-[10px] tracking-wider text-muted-foreground uppercase">{title}</h3>
      {children}
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right text-foreground">{children}</span>
    </div>
  );
}

/** Everything about one row: the full destination, every error and warning, the price and the raw cells. */
export function BatchItemDrawer({ item, batch, open, onOpenChange }: BatchItemDrawerProps) {
  const { t } = useTranslations("payoutBatches");
  if (!item) return null;

  const delivery = batch.currency;
  const source = batch.source_currency;
  const destinationLabel = batchDestinationLabel(item.destination);
  const beneficiary = item.beneficiary_name ?? batchDestinationBeneficiary(item.destination);

  return (
    <ColocaDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={t("row_drawer_title", { row: item.row_number })}
      description={beneficiary ?? t("row_beneficiary_unknown")}
      size="md"
      pinLastChild={false}
    >
      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-2">
          <ItemStatusPill status={item.status} />
          {item.attempt > 1 && (
            <span className="text-xs text-muted-foreground">
              {t("row_attempt", { attempt: item.attempt })}
            </span>
          )}
        </div>

        {item.failure_message && (
          <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
            <div className="min-w-0 space-y-1">
              <p>{item.failure_message}</p>
              {item.failure_code && <p className="font-mono text-[11px] opacity-80">{item.failure_code}</p>}
            </div>
          </div>
        )}

        <Section title={t("row_destination_title")}>
          <Row label={t(destinationLabel.key, destinationLabel.params)}>
            <span className="font-mono text-xs tabular-nums break-all">
              {batchDestinationValue(item.destination) || "—"}
            </span>
          </Row>
          {item.receive_amount && (
            <Row label={t("items_col_receive")}>
              <ColocaAmountDisplay
                value={item.receive_amount}
                currency={delivery}
                fractionDigits={minorUnitsFor(delivery)}
                size="sm"
              />
            </Row>
          )}
          {item.payout_id && (
            <Link
              to={payoutActivityPath(item.payout_id)}
              className="inline-block text-sm font-medium text-primary underline-offset-2 hover:underline"
            >
              {t("row_payout_link")}
            </Link>
          )}
        </Section>

        {item.errors.length > 0 && (
          <Section title={t("row_errors_title")}>
            <ul className="space-y-1.5">
              {item.errors.map((error, index) => (
                <li
                  key={`${error.code}-${index}`}
                  className="flex items-start gap-2 text-sm text-destructive"
                >
                  <AlertCircle aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
                  <span>
                    {error.field && <span className="font-mono text-xs">{error.field} · </span>}
                    {error.message}
                  </span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {item.warnings.length > 0 && (
          <Section title={t("row_warnings_title")}>
            <ul className="space-y-1.5">
              {item.warnings.map((warning, index) => (
                <li key={`${warning.code}-${index}`} className="flex items-start gap-2 text-sm text-warning">
                  <AlertTriangle aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
                  <span className="space-x-2">
                    <span>{warning.message}</span>
                    {warning.payout_id && (
                      <Link
                        to={payoutActivityPath(warning.payout_id)}
                        className="font-medium text-primary underline-offset-2 hover:underline"
                      >
                        {t("row_payout_link")}
                      </Link>
                    )}
                    {warning.batch_id && warning.batch_id !== batch.id && (
                      <Link
                        to={batchPath(warning.batch_id)}
                        className="font-medium text-primary underline-offset-2 hover:underline"
                      >
                        {t("row_batch_link")}
                      </Link>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        <Section title={t("row_pricing_title")}>
          {item.pricing ? (
            <div className="space-y-1.5">
              <Row label={t("items_col_fee")}>
                <ColocaAmountDisplay
                  value={item.pricing.fee_total}
                  currency={delivery}
                  fractionDigits={minorUnitsFor(delivery)}
                  size="sm"
                />
              </Row>
              <Row label={t("items_col_debit")}>
                <ColocaAmountDisplay
                  value={item.pricing.total_debit_source}
                  currency={source}
                  fractionDigits={minorUnitsFor(source)}
                  size="sm"
                />
              </Row>
              {item.pricing.rate && (
                <Row label={t("items_col_rate")}>
                  <span className="font-mono text-xs tabular-nums">
                    1 {source} = {item.pricing.rate} {delivery}
                  </span>
                </Row>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{t("row_no_pricing")}</p>
          )}
        </Section>

        <Section title={t("row_raw_title")}>
          <dl className="grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-x-3 gap-y-1 text-xs">
            {Object.entries(item.raw_row).map(([key, value]) => (
              <div key={key} className="contents">
                <dt className="truncate font-mono text-muted-foreground">{key}</dt>
                <dd className="font-mono break-all text-foreground tabular-nums">
                  {value == null ? "—" : String(value)}
                </dd>
              </div>
            ))}
          </dl>
        </Section>
      </div>
    </ColocaDrawer>
  );
}
